import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { formatDate3 } from '../../../../../../utils';

interface IFinancialInformationSection1 {
  debtType: string;
  id: string;
  interestRate: number;
  maturityDate: string;
  monthlyPayment: number;
  originalAmount: number;
  originationDate: string;
  payableTo: string;
  presentBalance: number;
}

interface IFinancialInformationSection2 {
  currentRent: number;
  id: string;
  tenantName: string;
  tenantType: string;
  unitNumber: string;
  willIncreaseRent: string;
}

interface IFinancialInformationProps {
  step2: {
    section1: IFinancialInformationSection1[];
    section2: IFinancialInformationSection2[];
  };
}
export default function FinancialInformation({ step2 }: IFinancialInformationProps) {
  const totalOriginalAmount = step2?.section1?.reduce((acc: number, curr: IFinancialInformationSection1) => {
    return acc + curr?.originalAmount;
  }, 0);

  const totalPresentBalance = step2?.section1?.reduce((acc: number, curr: IFinancialInformationSection1) => {
    return acc + curr?.presentBalance;
  }, 0);

  const totalMonthlyPayments = step2?.section1?.reduce((acc: number, curr: IFinancialInformationSection1) => {
    return acc + curr?.monthlyPayment;
  }, 0);

  const totalCurrentRent = step2?.section2?.reduce((acc: number, curr: IFinancialInformationSection2) => {
    return acc + curr?.currentRent;
  }, 0);

  return (
    <section aria-labelledby='applicant-information-title'>
      <div className='bg-white shadow sm:rounded-lg'>
        <div className='py-5'>
          <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
            <h2 id='applicant-information-title' className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'>
              Financial Information
            </h2>
          </div>
        </div>
        <div className='border-t border-gray-200 px-4 py-5 sm:px-6 flex flex-col gap-3'>
          <div>
            <h2 className='mt-1 max-w-2xl '>Current Indebtedness</h2>
            {step2?.section1.length === 0 && <div className='text-sm font-medium text-gray-500'>None</div>}
            {step2?.section1.length > 0 && (
              <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter overflow-x-scroll'>
                <table className='divide-y divide-gray-200 w-full'>
                  <thead className='bg-gray-100'>
                    <tr className='flex justify-between px-5 py-4'>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-32'>Type</th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-40'>
                        To Whom Payable
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-40'>
                        Original Amount ($)
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-32'>
                        Origination Date
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-40'>
                        Present Balance ($)
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-40'>
                        Interest Rate (%)
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-40'>Maturity Date</th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-40'>
                        Monthly Payments ($)
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {step2?.section1.map((finance: IFinancialInformationSection1) => (
                      <tr key={finance.id} className='flex items-center w-full justify-between px-5 py-2'>
                        <td className='w-32 flex items-start capitalize'>{finance?.debtType}</td>
                        <td className='w-40 flex items-start capitalize'>{finance?.payableTo}</td>
                        <td className='w-40 flex items-start capitalize'>
                          <CurrencyFormatDisplay value={finance?.originalAmount ?? 0} />
                        </td>
                        <td className='w-32 flex items-start capitalize'>{formatDate3(finance?.originationDate)}</td>
                        <td className='w-40 flex items-start capitalize'>
                          <CurrencyFormatDisplay value={finance.presentBalance ?? 0} />
                        </td>
                        <td className='w-40 flex items-start capitalize'>{finance?.interestRate ?? 0}</td>
                        <td className='w-40 flex items-start capitalize'>{formatDate3(finance?.maturityDate)}</td>
                        <td className='w-40 flex items-start capitalize'>
                          <CurrencyFormatDisplay value={finance?.monthlyPayment ?? 0} />
                        </td>
                      </tr>
                    ))}
                    <tr className='flex items-center w-full justify-between px-5 py-2'>
                      <td className='w-32 font-bold'>Total</td>
                      <td className='w-40'></td>
                      <td className='w-40 font-bold'>
                        <CurrencyFormatDisplay value={totalOriginalAmount ?? 0} />
                      </td>
                      <td className='w-32'></td>
                      <td className='w-40 font-bold'>
                        {' '}
                        <CurrencyFormatDisplay value={totalPresentBalance ?? 0} />
                      </td>
                      <td className='w-40'></td>
                      <td className='w-40'></td>
                      <td className='w-40 font-bold'>
                        {' '}
                        <CurrencyFormatDisplay value={totalMonthlyPayments ?? 0} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div>
            <h2 className='max-w-2xl '>Current Tenancy</h2>
            {step2?.section2.length === 0 && <div className='text-sm font-medium text-gray-500'>None</div>}
            {step2?.section2.length > 0 && (
              <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter overflow-x-scroll'>
                <table className='divide-y divide-gray-200 w-full'>
                  <thead className='bg-gray-100'>
                    <tr className='flex justify-between px-5 py-4'>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-32'>
                        Unit Number & Name
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-24'>Type</th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-24'>
                        Current Rent ($)
                      </th>
                      <th className='flex py-2 font-bold tracking-wider text-neutralColor-normal text-left text-xs w-80'>
                        Will building renovations result in increased rent?
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {step2?.section2.map((tenancy: IFinancialInformationSection2) => (
                      <tr key={tenancy.id} className='flex items-center w-full justify-between px-5 py-2'>
                        <td className='w-32 flex items-start capitalize'>
                          {tenancy?.unitNumber}, {tenancy?.tenantName}
                        </td>
                        <td className='w-24 flex items-start capitalize'>{tenancy?.tenantType}</td>
                        <td className='w-24 flex items-start capitalize'>
                          <CurrencyFormatDisplay value={tenancy?.currentRent ?? 0} />
                        </td>
                        <td className='w-80 flex items-start capitalize'>{tenancy?.willIncreaseRent}</td>
                      </tr>
                    ))}
                    <tr className='flex items-center w-full justify-between px-5 py-2'>
                      <td className='w-32 font-bold'>Total</td>
                      <td className='w-24'></td>
                      <td className='w-24 font-bold'>
                        <CurrencyFormatDisplay value={totalCurrentRent ?? 0} />
                      </td>
                      <td className='w-80'></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
